<script>
import axios from 'axios';
import { FormButton } from '../form';
import PaginationTable from '../PaginationTable.vue';
import TextInput from '../form/TextInput.vue';
import ModalDialog from '../ModalDialog.vue';
import { GlobalPolicy } from '@/policies/global';
import CheckboxInput from '../form/CheckboxInput.vue';

const query = {
    search: ''
}

export default {
    setup() {
        return {
            GlobalPolicy,
            panes: [
                {
                    name: 'division',
                    endpoint: 'divisions/communities',
                    label: 'Communities',
                    category: 'community'
                },
                {
                    name: 'collection',
                    endpoint: 'collections',
                    label: 'Collections',
                    category: 'community'
                },
                {
                    name: 'floorplan',
                    endpoint: 'floorplans',
                    label: 'Floorplans',
                    category: 'home'
                },
                {
                    name: 'sales_order',
                    endpoint: 'sales_orders',
                    label: 'Available Homes',
                    category: 'home'
                }
            ]
        }
    },
    data() {
        return {
            attached: {
                floorplan: [],
                sales_order: [],
                collection: [],
                division: []
            },
            isExpanded: {
                floorplan: false,
                sales_order: false,
                collection: false,
                division: false
            },
            collections: [],
            items: [],
            query: {...query},
            context: null,
            selected: []
        }
    },
    methods: {
        index() {
            axios.get(`promotions/${this.id}/attachments`)
                .then(resp => {
                    this.attached = resp.data;
                })
                .catch(err => {
                    console.error(err);
                });
        },
        loadCollections() {
            axios.get('collections')
                .then(resp => {
                    this.collections = resp.data.sort((a, b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);
                })
                .catch(err => {
                    console.error(err);
                });
        },
        openAttach(context) {
            this.context = context;
            this.indexItems();
        },
        indexItems() {
            if (!this.context) return;
            let endpoint = this.context.endpoint;
            axios.get(endpoint)
                .then(resp => {
                    this.items = endpoint === 'sales_orders' ?
                        resp.data.sort((a, b) => a.kova.Lot.StreetAddress > b.kova.Lot.StreetAddress ? 1 : -1) :
                        resp.data.sort((a, b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);
                })
                .catch(err => {
                    console.error(err);
                });
        },
        cancel() {
            this.context = null;
            this.items = [];
            this.selected = [];
            this.query = {...query};
        },
        sync() {
            if (!this.context) return;
            axios.post(`promotions/${this.id}/attachments`, {items: this.selected, type: this.context.name})
                .then(resp => {
                    this.attached[this.context.name] = resp.data;
                    this.cancel();
                })
                .catch(err => {
                    console.error(err);
                });
        },
        unsync(index, context) {
            let item = this.attached[context][index];
            axios.delete(`promotions/${this.id}/attachments/${item.id}`)
                .then(() => {
                    this.attached[context].splice(index, 1);
                })
                .catch(err => {
                    console.error(err);
                });
        },
        getCollection(rid) {
            return this.collections.find(collection => collection.community_rid === rid) || '-';
        },
    },
    computed: {
        filtered() {
            let ids = this.attached[this.context?.name ?? null].map(attach => attach.model.id);
            let items = this.items?.filter(item => !ids.includes(item.id) && item.status !== 'inactive') || [];

            if (this.query.search) {
                items = items.filter(item => item.meta?.info?.name.toLowerCase().includes(this.query.search.toLowerCase()));
            }

            return items;
        },
        selectAll: {
            get() {
                return this.selected
            },
            set(value) {
                value = [...new Set(value)];
                this.selected = this.selected.sort((a, b) => a > b ? 1 : -1).join(',') === value.sort((a, b) => a > b ? 1 : -1).join(',') ?
                    [] :
                    value.filter(id => !this.selected.includes(id))
            }
        }
    },
    props: {
        id: {
            type: Number,
            required: true
        }
    },
    created() {
        this.index();
        this.loadCollections();
    },
    components: {
        CheckboxInput,
        FormButton,
        TextInput,
        ModalDialog,
        PaginationTable
    }
}
</script>

<template>
    <h2 class="font-headline leading-9 antialiased text-xl bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium mb-2 py-3">Attached Locations</h2>
    <div class="bg-white rounded-lg px-4 py-2">
        <template v-for="pane, i in panes" :key="`pane-${i}`">
            <div class="w-full p-1 rounded-lg flex justify-between items-center cursor-pointer py-2" @click="isExpanded[pane.name] = !isExpanded[pane.name]">
                <div class="flex items-center gap-2 mr-2">
                    <i class="fas fa-xs text-bhav-blue-200" :class="isExpanded[pane.name] ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                </div>
                <h3 class="text-bhav-blue-200 flex w-[228px] text-md leading-7">{{ pane.label }}</h3>
                <p class="text-bhav-blue-500 flex-1 text-md leading-7">{{ attached[pane.name].length }} {{ pane.label }}</p>
                <div class="flex items-center gap-2 ml-2">
                    <FormButton v-if="GlobalPolicy.edit()" @click.stop="openAttach(pane)" font-style="font-normal" prefix-class="fak" icon="fa-link" color="blue">
                        Bulk Attach
                    </FormButton>
                </div>
            </div>
            <div v-if="isExpanded[pane.name]" class="w-full">
                <PaginationTable :data="attached[pane.name]" :default="5">
                    <template #headers>
                        <th scope="col" class="w-1/3 px-6 py-3">
                            Name
                        </th>
                        <th v-if="['floorplan','sales_order'].includes(pane.name)" scope="col" class="w-1/3 px-6 py-3">
                            Collection
                        </th>
                        <th scope="col" class="px-6 w-1/3 text-right">
                            <span class="sr-only">Actions</span>
                        </th>
                    </template>

                    <template v-if="pane.name === 'floorplan'" #columns="{row, i:k}">
                        <td class="whitespace-nowrap text-sm">
                            <router-link :to="{name: 'floorplan-tab', params: {id: row.model.id, tab: 'promotions'}}" class="text-bhav-blue-500 px-6 py-4 block">
                                {{ row.model?.meta?.info?.name }}
                            </router-link>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                            {{ row?.model?.collection?.meta?.info?.name }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm leading-5 font-medium flex gap-2 justify-end">
                            <FormButton v-if="GlobalPolicy.edit()" @click="unsync(k, pane.name)" title="Detach this promotion from the item" prefix-class="fak" icon="fa-detach" color="red">
                                <span class="sr-only">Detach</span>
                            </FormButton>
                        </td>
                    </template>

                    <template v-else-if="pane.name === 'sales_order'" #columns="{row, i:k}">
                        <td class="whitespace-nowrap text-sm">
                            <router-link :to="{name: 'available-homes-tab', params: {id: row.model.id, tab: 'promotions'}}" class="text-bhav-blue-500 px-6 py-4 block">
                                {{ row.model?.kova?.Lot?.StreetAddress }}, {{ row.model?.kova?.Lot?.City }}, {{ row.model?.kova?.Lot?.State }} {{ row.model?.kova?.Lot?.ZipCode }}
                            </router-link>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                            {{ row?.model?.collection?.meta?.info?.name }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm leading-5 font-medium flex gap-2 justify-end">
                            <FormButton v-if="GlobalPolicy.edit()" @click="unsync(k, pane.name)" title="Detach this promotion from the item" prefix-class="fak" icon="fa-detach" color="red">
                                <span class="sr-only">Detach</span>
                            </FormButton>
                        </td>
                    </template>

                    <template v-else-if="pane.name === 'collection'" #columns="{row, i:k}">
                        <td class="whitespace-nowrap text-sm">
                            <router-link :to="{name: 'collection-tab', params: {id: row.model.id, tab: 'promotions'}}" class="text-bhav-blue-500 px-6 py-4 block">
                                {{ row.model.meta?.info?.name }}
                            </router-link>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm leading-5 font-medium flex gap-2 justify-end">
                            <FormButton v-if="GlobalPolicy.edit()" @click="unsync(k, pane.name)" title="Detach this promotion from the item" prefix-class="fak" icon="fa-detach" color="red">
                                <span class="sr-only">Detach</span>
                            </FormButton>
                        </td>
                    </template>

                    <template v-else-if="pane.name === 'division'" #columns="{row, i:k}">
                        <td class="whitespace-nowrap text-sm">
                            <router-link :to="{name: 'community-tab', params: {id: row.model.id, tab: 'promotions'}}" class="text-bhav-blue-500 px-6 py-4 block">
                                {{ row.model.meta?.info?.name }}
                            </router-link>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm leading-5 font-medium flex gap-2 justify-end">
                            <FormButton v-if="GlobalPolicy.edit()" @click="unsync(k, pane.name)" title="Detach this promotion from the item" prefix-class="fak" icon="fa-detach" color="red">
                                <span class="sr-only">Detach</span>
                            </FormButton>
                        </td>
                    </template>
                </PaginationTable>
            </div>
        </template>
    </div>

    <ModalDialog v-if="context !== null" :no-padding="true" :open="context !== null" @close="cancel">
        <template #header>
            <h2>Bulk Attach to {{ context.label }}</h2>
        </template>
        <template #body>
            <div class="flex w-full items-center gap-4">
                <div class="flex-1">
                    <TextInput v-model="query.search" placeholder="Search by keyword" />
                </div>
            </div>
            <PaginationTable :data="filtered" :default="5">
                <template #headers="{rows}">
                    <th scope="col" class="px-6 py-3 text-le">
                        <CheckboxInput color="white" label="Select All" :showLabel="false" v-model="selectAll" :value="[...rows ?? []].map(row => row.id)" />
                    </th>
                    <th scope="col" class="px-6 py-3 text-le">
                        Name
                    </th>
                    <th v-if="['floorplan','sales_order'].includes(context.name)" scope="col" class="px-6 py-3 text-le">
                        Collection
                    </th>
                </template>
                <template #columns="{row}">
                    <th scope="row" class="px-6 py-3">
                        <CheckboxInput :showLabel="false" :label="context.name == 'sales_order' ? row.kova.Lot.StreetAddress : row.meta?.info?.name || row.kova.Name" v-model="selected" :value="row.id" />
                    </th>
                    <td class="px-6 py-3">
                        {{ context.name == 'sales_order' ? `${row.kova.Lot.StreetAddress}` : row.meta?.info?.name || row.kova.Name }}
                    </td>
                    <td v-if="['floorplan','sales_order'].includes(context.name)" class="px-6 py-3">
                        {{ getCollection(row.kova?.CommunityRID)?.meta?.info?.name }}
                    </td>
                </template>
            </PaginationTable>
        </template>
        <template #buttons>
            <div class="flex gap-1 px-3">
                <FormButton @click="cancel" color="gray">Cancel</FormButton>
                <FormButton @click="sync" :disabled="!selected.length" color="blue">Attach to Selected</FormButton>
            </div>
        </template>
    </ModalDialog>
</template>
